import { getAccountMetadata, getUserMetadata } from '@/data/metadata/selectors';
import type Shift from '@/data/shift/model';
import { getStateShifts } from '@/data/shift/selectors/getStateShifts';
import type User from '@/data/user/model';
import { getActiveUsers } from '@/data/user/selectors';
import type { EmployeeOnboardingStatus } from '@/onboarding/constants';
import {
  STEP_ATTENDANCE_ADD_EMPLOYEES,
  STEP_ATTENDANCE_EDIT_TIMESHEET,
  STEP_ATTENDANCE_EXPORT_TIMESHEET,
} from '@/onboarding/constants';
import {
  FOCUSED_ONBOARDING_REDIRECT,
  IMPORTED_SCHEDULE,
  MAGIC_IMPORT_COMPLETED,
  ONBOARDING_ATTENDANCE_CHECKLIST_CLOSED,
  ONBOARDING_ATTENDANCE_CHECKLIST_MINIMIZED,
  ONBOARDING_PRODUCT_ATTENDANCE_DESIRED,
  ONBOARDING_PRODUCT_SCHEDULING_DESIRED,
  ONBOARDING_SCHEDULE_PUBLISHED,
  ONBOARDING_SHIFT_CREATED,
  ONBOARDING_SHIFT_REASSIGNED,
  ONBOARDING_TIMESHEET_EDITED,
  ONBOARDING_TIMESHEET_EXPORTED,
  ONBOARDING_USER_CREATED,
} from '@/onboarding/metadataConstants';
import { getAccountId, getAuthAccount } from '@/shared/auth/selectors';
import type { WiwState } from '@/store';

import { createSelector } from '@reduxjs/toolkit';
import moment from 'moment-timezone';

function booleanifyAccountMetadata(state: WiwState, key: string, defaultWhenNotLoaded: boolean): boolean {
  if (!state.data.metadata.get('accountLoaded')) {
    return defaultWhenNotLoaded;
  }
  return getAccountMetadata(state).get(key) === true;
}

function booleanifyUserMetadata(state: WiwState, key: string, defaultWhenNotLoaded: boolean): boolean {
  if (!state.data.metadata.get('userLoaded')) {
    return defaultWhenNotLoaded;
  }
  return getUserMetadata(state).get(key) === true;
}

// "Add Employees Step" completed, but only according to metadata only.
// Used by saveUser action creator to avoid excess metadata updates.
export function getOnboardingUserCreatedAccordingToMetadata(state: WiwState): boolean {
  return booleanifyAccountMetadata(state, ONBOARDING_USER_CREATED, false);
}

/**
 * Some state we can derive (such as number of users created) and also look at metadata.
 */
export const getOnboardingUserCreated = createSelector(
  getOnboardingUserCreatedAccordingToMetadata,
  getActiveUsers,
  (userCreatedAccordingToMetadata, users) =>
    userCreatedAccordingToMetadata || users.count((user: User) => !!user.id) > 1,
);

/**
 * These selectors are for the "new" metadata service. The idea is to have onboarding state kept
 * in "new" metadata long-term, rather than cookies, local storage or the "old" metadata service.
 *
 * Use of these selectors should still call fetchUserMetadataIfNeeded or fetchAccountMetadataIfNeeded in
 * componentDidMount() lifecycle methods to ensure the appropriate metadata is loaded.
 */
export function getOnboardingAttendanceChecklistClosed(state: WiwState): boolean {
  return booleanifyUserMetadata(state, ONBOARDING_ATTENDANCE_CHECKLIST_CLOSED, true);
}

export function getOnboardingAttendanceChecklistMinimized(state: WiwState): boolean {
  return booleanifyUserMetadata(state, ONBOARDING_ATTENDANCE_CHECKLIST_MINIMIZED, true);
}

export function getOnboardingTimesheetEdited(state: WiwState): boolean {
  return booleanifyAccountMetadata(state, ONBOARDING_TIMESHEET_EDITED, true);
}

export function getOnboardingTimesheetExported(state: WiwState): boolean {
  return booleanifyAccountMetadata(state, ONBOARDING_TIMESHEET_EXPORTED, true);
}

export const getOnboardingAttendanceChecklistActiveStep = createSelector(
  [getOnboardingUserCreated, getOnboardingTimesheetEdited, getOnboardingTimesheetExported],
  (userCreated, timesheetEdited, timesheetExported) => {
    let activeStep = null;
    if (!userCreated) {
      activeStep = STEP_ATTENDANCE_ADD_EMPLOYEES;
    } else if (userCreated && !timesheetEdited) {
      activeStep = STEP_ATTENDANCE_EDIT_TIMESHEET;
    } else if (timesheetEdited && userCreated && !timesheetExported) {
      activeStep = STEP_ATTENDANCE_EXPORT_TIMESHEET;
    }

    return activeStep;
  },
);

export const getOnboardingAttendanceChecklistAllStepsCompleted = createSelector(
  [getOnboardingUserCreated, getOnboardingTimesheetEdited, getOnboardingTimesheetExported],
  (addedEmployees, editedTimesheet, exportedTimesheet) => {
    return addedEmployees && editedTimesheet && exportedTimesheet;
  },
);

// Only used to update metadata
export function getOnboardingShiftCreatedAccordingToMetadata(state: WiwState): boolean {
  return booleanifyAccountMetadata(state, ONBOARDING_SHIFT_CREATED, false);
}

export const getOnboardingShiftCreated = createSelector(
  [getStateShifts, getOnboardingShiftCreatedAccordingToMetadata],
  (shifts, metadataShiftCreated) => metadataShiftCreated || shifts.count((shift: Shift) => !!shift.id) > 0,
);

// Only used to update metadata
export function getOnboardingSchedulePublishedAccordingToMetadata(state: WiwState): boolean {
  return booleanifyAccountMetadata(state, ONBOARDING_SCHEDULE_PUBLISHED, false);
}

export const getOnboardingSchedulePublished = createSelector(
  [getStateShifts, getOnboardingSchedulePublishedAccordingToMetadata],
  (shifts, metadataSchedulePublished) =>
    metadataSchedulePublished || shifts.filter((shift: Shift) => shift.published === true).size > 0,
);

export const getOnboardingSchedulingChecklistAllStepsCompleted = createSelector(
  [getOnboardingUserCreated, getOnboardingShiftCreated, getOnboardingSchedulePublished],
  (addedEmployees, shiftCreated, schedulePublished) => {
    return addedEmployees && shiftCreated && schedulePublished;
  },
);

export function getOnboardingProductAttendanceDesired(state: WiwState): boolean {
  return booleanifyAccountMetadata(state, ONBOARDING_PRODUCT_ATTENDANCE_DESIRED, false);
}

export function getOnboardingProductSchedulingDesired(state: WiwState): boolean {
  return booleanifyAccountMetadata(state, ONBOARDING_PRODUCT_SCHEDULING_DESIRED, false);
}

const ONBOARDING_ACCOUNT_DAY_LIMIT = 30;
export const getOnboardingIsRecentAccount = createSelector(
  [getAuthAccount],
  currentAccount =>
    currentAccount && moment().diff(currentAccount.date('created_at'), 'days') <= ONBOARDING_ACCOUNT_DAY_LIMIT,
);

export function getIsLocalStorageMigrated(state: WiwState): boolean {
  return localStorage.getItem(`checklist_state${getAccountId(state)}`) == null;
}

// Helper to avoid flashing onboarding components when they shouldn't be displayed
export const getOnboardingMigratedAndLoaded = createSelector(
  [
    getIsLocalStorageMigrated,
    (state: WiwState) => state.data.metadata.get('accountLoaded') && state.data.metadata.get('userLoaded'),
  ],
  (migrated, loaded) => migrated && loaded,
);

export function getOnboardingShiftReassigned(state: WiwState): boolean {
  return booleanifyAccountMetadata(state, ONBOARDING_SHIFT_REASSIGNED, true);
}

export function getFocusedOnboardingRedirect(state: WiwState): boolean {
  return booleanifyAccountMetadata(state, FOCUSED_ONBOARDING_REDIRECT, false);
}

export function getHasImportedSchedule(state: WiwState): boolean {
  return booleanifyAccountMetadata(state, IMPORTED_SCHEDULE, false);
}

export function getMagicImportCompleted(state: WiwState): boolean {
  return booleanifyAccountMetadata(state, MAGIC_IMPORT_COMPLETED, false);
}

export function getEmployeeOnboardingStatus(state: WiwState): EmployeeOnboardingStatus | undefined {
  return state.onboarding.employeeOnboardingStatus;
}
